import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  User, 
  Shield, 
  Key, 
  Clock, 
  Smartphone,
  AlertTriangle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';

const AdminMyAccount = () => {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  
  // 2FA setup states
  const [setting2FA, setSetting2FA] = useState(false);
  const [totpSetup, setTotpSetup] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    fetchAccount();
  }, []);

  const fetchAccount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/my-account`, getAuthHeader());
      setAccount(response.data);
    } catch (error) {
      console.error('Error fetching account:', error);
      toast.error('Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/my-account/password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, getAuthHeader());
      toast.success('Password changed successfully');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSetup2FA = async () => {
    setSetting2FA(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/2fa/setup`, {}, getAuthHeader());
      setTotpSetup(response.data);
    } catch (error) {
      toast.error('Failed to initialize 2FA setup');
      setSetting2FA(false);
    }
  };

  const handleVerify2FA = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setVerifying2FA(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/2fa/verify`, { totp_code: verifyCode }, getAuthHeader());
      toast.success('2FA enabled successfully!');
      setTotpSetup(null);
      setSetting2FA(false);
      setVerifyCode('');
      fetchAccount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifying2FA(false);
    }
  };

  const cancelSetup = () => {
    setTotpSetup(null);
    setSetting2FA(false);
    setVerifyCode('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4a5d42]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#c5bfa9]">My Account</h1>
        <p className="text-[#8b9a7e] mt-1">Manage your account settings and security</p>
      </div>

      {/* Force Password Change Warning */}
      {account?.force_password_change && (
        <div className="bg-orange-900/20 border border-orange-600/50 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-orange-400 font-medium">Password Change Required</p>
            <p className="text-orange-200/80 text-sm">
              You must change your password before you can access other areas of the admin panel.
            </p>
          </div>
        </div>
      )}

      {/* 2FA Required Warning (when enforcement is on but user doesn't have 2FA) */}
      {account?.enforce_2fa_enabled && !account?.totp_enabled && !account?.force_password_change && (
        <div className="bg-yellow-900/20 border border-yellow-600/50 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-yellow-400 font-medium">2FA Setup Required</p>
            <p className="text-yellow-200/80 text-sm">
              Two-factor authentication is required for all accounts. Please set up 2FA below
              {account?.totp_deadline && (
                <> by <strong>{new Date(account.totp_deadline).toLocaleDateString()}</strong></>
              )}
              {' '}to maintain access to your account.
            </p>
          </div>
        </div>
      )}

      {/* Account Details */}
      <Card className="bg-[#232921] border-[#3d4a35]">
        <CardHeader>
          <CardTitle className="text-[#c5bfa9] flex items-center gap-2">
            <User className="w-5 h-5" />
            Account Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#8b9a7e] text-sm">Username</Label>
              <p className="text-[#e8e4dc] font-medium">{account?.username}</p>
            </div>
            <div>
              <Label className="text-[#8b9a7e] text-sm">Email</Label>
              <p className="text-[#e8e4dc] font-medium">{account?.email}</p>
            </div>
            <div>
              <Label className="text-[#8b9a7e] text-sm">Role</Label>
              <div className="mt-1">
                <Badge 
                  className={
                    account?.role === 'superadmin' 
                      ? 'bg-purple-600/20 text-purple-400 border-purple-600' 
                      : account?.role === 'admin'
                      ? 'bg-blue-600/20 text-blue-400 border-blue-600'
                      : 'bg-gray-600/20 text-gray-400 border-gray-600'
                  }
                >
                  {account?.role}
                </Badge>
              </div>
            </div>
            <div>
              <Label className="text-[#8b9a7e] text-sm">Last Login</Label>
              <p className="text-[#e8e4dc] flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {account?.last_login 
                  ? new Date(account.last_login).toLocaleString() 
                  : 'Never'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 2FA Section */}
      <Card className="bg-[#232921] border-[#3d4a35]">
        <CardHeader>
          <CardTitle className="text-[#c5bfa9] flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription className="text-[#8b9a7e]">
            Add an extra layer of security to your account
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Current Status */}
          <div className="flex items-center justify-between p-4 bg-[#1a1f16] rounded-lg">
            <div className="flex items-center gap-3">
              <Smartphone className="w-6 h-6 text-[#8b9a7e]" />
              <div>
                <p className="text-[#e8e4dc] font-medium">Authenticator App</p>
                <p className="text-[#8b9a7e] text-sm">
                  {account?.totp_enabled 
                    ? 'Your account is protected with 2FA' 
                    : 'Not enabled'}
                </p>
              </div>
            </div>
            {account?.totp_enabled ? (
              <Badge className="bg-green-600/20 text-green-400 border-green-600 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Enabled
              </Badge>
            ) : (
              <Button 
                onClick={handleSetup2FA} 
                disabled={setting2FA}
                className="bg-[#4a5d42] hover:bg-[#5a6f4e]"
              >
                {setting2FA ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Set Up 2FA
              </Button>
            )}
          </div>

          {/* 2FA Setup Flow */}
          {totpSetup && (
            <div className="border border-[#3d4a35] rounded-lg p-4 space-y-4">
              <h4 className="text-[#c5bfa9] font-medium">Set Up Two-Factor Authentication</h4>
              
              <div className="space-y-3">
                <p className="text-[#8b9a7e] text-sm">
                  1. Install an authenticator app like Google Authenticator or Authy on your phone
                </p>
                <p className="text-[#8b9a7e] text-sm">
                  2. Scan the QR code below with your authenticator app
                </p>
                
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img 
                    src={`data:image/png;base64,${totpSetup.qr_code}`} 
                    alt="2FA QR Code" 
                    className="w-48 h-48"
                  />
                </div>
                
                <p className="text-[#8b9a7e] text-sm">
                  Or enter this code manually: <code className="bg-[#1a1f16] px-2 py-1 rounded text-[#c5bfa9]">{totpSetup.secret}</code>
                </p>
                
                <p className="text-[#8b9a7e] text-sm">
                  3. Enter the 6-digit code from your authenticator app to verify:
                </p>
                
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    className="w-32 text-center text-lg tracking-widest bg-[#1a1f16] border-[#3d4a35] text-[#e8e4dc]"
                  />
                  <Button 
                    onClick={handleVerify2FA}
                    disabled={verifying2FA || verifyCode.length !== 6}
                    className="bg-[#4a5d42] hover:bg-[#5a6f4e]"
                  >
                    {verifying2FA ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Verify & Enable
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={cancelSetup}
                    className="border-[#3d4a35] text-[#8b9a7e] hover:bg-[#3d4a35]/50"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card className="bg-[#232921] border-[#3d4a35]">
        <CardHeader>
          <CardTitle className="text-[#c5bfa9] flex items-center gap-2">
            <Key className="w-5 h-5" />
            Change Password
          </CardTitle>
          <CardDescription className="text-[#8b9a7e]">
            Update your password to keep your account secure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
            <div className="space-y-2">
              <Label htmlFor="current_password" className="text-[#c5bfa9]">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordForm.current_password}
                onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                required
                className="bg-[#1a1f16] border-[#3d4a35] text-[#e8e4dc]"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password" className="text-[#c5bfa9]">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordForm.new_password}
                onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                required
                minLength={8}
                className="bg-[#1a1f16] border-[#3d4a35] text-[#e8e4dc]"
              />
              <p className="text-xs text-[#8b9a7e]">Minimum 8 characters</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-[#c5bfa9]">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordForm.confirm_password}
                onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                required
                minLength={8}
                className="bg-[#1a1f16] border-[#3d4a35] text-[#e8e4dc]"
              />
            </div>
            
            <Button 
              type="submit" 
              disabled={changingPassword}
              className="bg-[#4a5d42] hover:bg-[#5a6f4e]"
            >
              {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminMyAccount;
